import { useEffect, useState } from "react";
import { Button, Typography, FormHelperText, MenuItem, Divider, Grid, Tabs, Tab } from "@mui/material";
import { HighlightOff } from "@mui/icons-material";
import { handleLayerEvent } from "actions/ViewerTools";
import * as GlobalActions from "actions/GlobalActions";
import { useStylesColorLegend } from "./ColorLegend";
import { DashInput, CustomSelect, Section } from "components";
import commonStyles from "styles/common";
import { useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

function getOperatorLabel(op: string) {
    if (op === "|") return "or";
    else return "&";
}

function parseAttributeQueryString(value: string) {
    let entries = value.split(" ");
    let items: any = [];
    entries.forEach((entry: any) => {
        let subItems = entry.split(",");
        if (subItems.length === 4)
            items.push({
                attr: subItems[0],
                min: subItems[1],
                max: subItems[2],
                operator: subItems[3],
            });
        else items.push({ attr: "", min: 0, max: 0, operator: "&" });
    });
    return items;
}

function createAttributeQueryString(items: any) {
    let result: string = "";
    let count: number = 0;
    items.forEach((entry: any) => {
        if (entry.attr === "") return;
        if (count++ > 0) {
            result += " ";
        }
        result += entry.attr + "," + entry.min + "," + entry.max + "," + entry.operator;
    });
    return result;
}

function isAlphaNumeric(attributeValues: any, attributeName: string) {
    return attributeValues.find(
        (attributeObject: any) => attributeObject.name === attributeName && attributeObject.values
    );
}

type globalState = {
    attributeQueryMode: number;
    attributeQueryString: string;
    attributeQueryBuilderValue: string;
};

let globalSettings: globalState[] = [];

type IAttributeQuery = {
    layer: any;
    selectedLayers: any;
    currentProject: any;
    attributeQueryEntity: string;
    colorLegend: any;
};

export default function AttributeQuery(props: IAttributeQuery) {
    const { classes } = useStylesColorLegend();
    const { classes: common } = commonStyles();
    var { layer, selectedLayers, attributeQueryEntity, colorLegend, currentProject } = props;
    const maxAttributesShown = 15;
    const {
        getAttributesObjects,
        getAttributesValues,
        getColorLegendSettings,
        requiresServerUpdate,
        isLimboState,
        getAttributes,
    } = GlobalActions;
    const attributes: any = getAttributesObjects(layer.ID);
    const attributeValues: any = getAttributesValues(layer.ID);

    const [update, setUpdate] = useState(false);
    const [showMore, setShowMore] = useState(attributes.length > maxAttributesShown);
    const [needsApply, setNeedsApply] = useState(false);

    const initialState = getColorLegendSettings(layer, colorLegend);
    const theme = useTheme();

    var isMultiple = false;
    if (selectedLayers.length === 1) {
        layer = selectedLayers[0];
    } else {
        layer = props.layer;
        isMultiple = true;
    }
    //console.log('isLimbo:', isLimbo);

    if (!globalSettings[layer.ID]) {
        globalSettings[layer.ID] = initialState;
    }

    if (isMultiple) {
        selectedLayers.forEach((currentLayer: any) => {
            if (!globalSettings[currentLayer.ID]) {
                globalSettings[currentLayer.ID] = initialState;
            }
        });
    }

    //console.log("Attribute Query:", globalSettings);

    useEffect(() => {
        if (update) {
            setUpdate(false);
        }
    }, [update]);

    const { attributeQueryMode, attributeQueryBuilderValue, attributeQueryString } = globalSettings[layer.ID];

    function makeDirty() {
        if (requiresServerUpdate(attributeQueryEntity, currentProject)) setNeedsApply(true);
    }

    function updateAttributeQueryBuilder() {
        const attributes = getAttributes(layer.ID);
        const attributeValues: any = getAttributesValues(layer.ID);

        //Lambda
        const getAttributeIndex = (attributeName: string) => {
            return attributes.findIndex((attribute: string) => attribute === attributeName);
        };

        //Lambda
        const getValueIndex = (attributeIndex: number, attributeValue: string) => {
            if (attributeIndex !== -1) {
                const attribute = attributeValues[attributeIndex];
                if (attribute) {
                    const stringAttributes = attribute.values;
                    if (stringAttributes) {
                        return stringAttributes.findIndex((val: string) => val === attributeValue);
                    }
                }
            }
            return -1;
        };

        //Lambda
        const getBuilderAttributeFilters = (builderColorAttributes: string) => {
            let attributeFilters: any[] = [];

            const elements: string[] = builderColorAttributes.split(" ");
            const elementCount: number = elements.length;
            const queryType: any = {
                Number: 0,
                String: 1,
            };
            for (let elementIndex: number = 0; elementIndex < elementCount; ++elementIndex) {
                const element: string = elements[elementIndex];
                if (element !== "") {
                    const subElements: string[] = element.split(",");
                    const attributeName: string = subElements[0];
                    const attributeIndex: number = getAttributeIndex(attributeName);
                    const attributeOperator: string = subElements[3];
                    if (subElements[1] === "") {
                        const value = subElements[2];
                        if (value !== "") {
                            const valueCount: number = attributes.length;
                            if (valueCount > 0) {
                                const valueIndex: number = getValueIndex(attributeIndex, value);
                                if (valueIndex !== -1) {
                                    attributeFilters.push({
                                        attributeIndex: attributeIndex,
                                        attributeName: attributeName,
                                        attributeSetValueName: value,
                                        attributeSetValueIndex: valueIndex,
                                        attributeOperator: attributeOperator,
                                        attributeType: queryType.String,
                                    });
                                }
                            } else {
                                attributeFilters.push({
                                    attributeIndex: attributeIndex,
                                    attributeName: attributeName,
                                    attributeStringValue: value,
                                    attributeOperator: attributeOperator,
                                    attributeType: queryType.String,
                                });
                            }
                        } else {
                            //INVALID ATTRIBUTESETVALUENAME
                            console.log("AQ. Filter Ignored");
                        }
                    } else {
                        const attributeMin: number = parseFloat(subElements[1]);
                        const attributeMax: any = parseFloat(subElements[2]);

                        attributeFilters.push({
                            attributeIndex: attributeIndex,
                            attributeName: attributeName,
                            attributeMin: attributeMin,
                            attributeMax: attributeMax,
                            attributeOperator: attributeOperator,
                            attributeType: queryType.Number,
                        });
                    }
                }
            }

            return attributeFilters;
        };
        var builderAttributes: string = globalSettings[layer.ID]["attributeQueryBuilderValue"];
        var builderAttributeFilters: any[] = getBuilderAttributeFilters(builderAttributes);
        let attributeQueryData = {
            type: 1,
            attributeQueryBuilderValue: builderAttributes,
            builderAttributeFilters: builderAttributeFilters,
            builderAttributeRawData: builderAttributes,
        };
        layer["attribute_query_mode"] = attributeQueryData.type + "";
        layer["attribute_query_builder"] = attributeQueryData.attributeQueryBuilderValue;
        handleLayerEvent("setAttributeQueryBuilder", attributeQueryData, layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["attribute_query_mode"] = attributeQueryData.type + "";
                currentLayer["attribute_query_builder"] = attributeQueryData.attributeQueryBuilderValue;
                handleLayerEvent("setAttributeQueryBuilder", attributeQueryData, currentLayer);
            });
        }
    }

    function updateAttributeQueryString() {
        const textAttributes: string = globalSettings[layer.ID]["attributeQueryString"];

        let attributeQueryData = {
            attributeQueryString: textAttributes,
            textAttributeRawData: textAttributes,
        };
        handleLayerEvent("setAttributeQueryString", attributeQueryData, layer);
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                handleLayerEvent("setAttributeQueryString", attributeQueryData, currentLayer);
            });
        }
    }

    function setQueryMode(mode: number) {
        layer["attribute_query_mode"] = mode;
        globalSettings[layer.ID]["attributeQueryMode"] = mode;
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                currentLayer["attribute_query_mode"] = mode;
                globalSettings[currentLayer.ID]["attributeQueryMode"] = mode;
            });
        }
        setUpdate(true);
        makeDirty();
    }

    function setUseQueryBuilder() {
        setQueryMode(1);
        //updateAttributeQueryBuilder();
    }

    function setUseQueryString() {
        setQueryMode(2);
        //updateAttributeQueryString();
    }

    function setQueryString(value: string) {
        globalSettings[layer.ID]["attributeQueryString"] = value;
        layer["attribute_query_string"] = value;
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["attributeQueryString"] = value;
                currentLayer["attribute_query_string"] = value;
            });
        }
        //updateAttributeQueryString();
        makeDirty();
        setUpdate(true);
    }

    function setQueryBuilderValue(value: string) {
        globalSettings[layer.ID]["attributeQueryBuilderValue"] = value;
        layer["attribute_query_builder"] = value;
        if (isMultiple) {
            selectedLayers.forEach((currentLayer: any) => {
                globalSettings[currentLayer.ID]["attributeQueryBuilderValue"] = value;
                currentLayer["attribute_query_builder"] = value;
            });
        }
        //updateAttributeQueryBuilder();
        makeDirty();
        setUpdate(true);
    }

    function setAttribute(newIndex: number, prevValue: string) {
        let items: any = parseAttributeQueryString(attributeQueryBuilderValue);
        var result = attributes.filter((attribute: any) => {
            return attribute.index === newIndex;
        });
        if (result.length > 0) {
            var attribute = result[0];
            var name = attribute.name;
            var index = items.findIndex((item: any) => item.attr === prevValue);
            if (index > -1) {
                var item = items[index];
                item["attr"] = name;
                if (attribute.values) {
                    item["min"] = "";
                    item["max"] = attribute.values[0];
                } else {
                    item["min"] = 0;
                    item["max"] = 100;
                }
                let result = createAttributeQueryString(items);
                setQueryBuilderValue(result);
                setUpdate(true);
                makeDirty();
            }
        }
    }

    function removeAttribute(index: number) {
        let entries = attributeQueryBuilderValue.split(" ");
        entries.splice(index, 1);
        let result: string = "";
        let count: number = 0;
        entries.forEach((entry: any) => {
            if (count++ > 0) result += " ";
            result += entry;
        });
        setQueryBuilderValue(result);
        setUpdate(true);
        makeDirty();
    }

    function setAttributeMin(index: number, value: number) {
        let items: any = parseAttributeQueryString(attributeQueryBuilderValue);
        items[index].min = value;
        let result = createAttributeQueryString(items);
        setQueryBuilderValue(result);
        setUpdate(true);
        makeDirty();
    }

    function setAttributeMax(index: number, value: number) {
        let items: any = parseAttributeQueryString(attributeQueryBuilderValue);
        items[index].max = value;
        let result = createAttributeQueryString(items);
        setQueryBuilderValue(result);
        setUpdate(true);
        makeDirty();
    }

    function setAttributeString(index: number, value: string) {
        let items: any = parseAttributeQueryString(attributeQueryBuilderValue);
        items[index].min = "";
        items[index].max = value;
        let result = createAttributeQueryString(items);
        setQueryBuilderValue(result);
        setUpdate(true);
        makeDirty();
    }

    function toggleAttributeOperator(index: number) {
        let items: any = parseAttributeQueryString(attributeQueryBuilderValue);
        if (items.length - 1 >= index) {
            items[index].operator = items[index].operator === "&" ? "|" : "&";
        }
        let result = createAttributeQueryString(items);
        setQueryBuilderValue(result);
        setUpdate(true);
        makeDirty();
    }

    function getValues(name: string) {
        var values: any = [];
        attributeValues.forEach((attrObject: any) => {
            if (attrObject.name === name && attrObject.values) {
                values = attrObject.values;
            }
        });
        return values;
    }

    function addAttributeFilterRow(name: string) {
        let newAttributeQueryBuilderValue: string = "";
        if (isAlphaNumeric(attributeValues, name)) {
            var value = "";
            if (getValues(name).length > 0) {
                value = getValues(name)[0];
            }
            if (attributeQueryBuilderValue !== "") {
                newAttributeQueryBuilderValue = attributeQueryBuilderValue + " " + name + ",," + value + ",&";
            } else {
                newAttributeQueryBuilderValue = name + ",," + value + ",&";
            }
        } else {
            if (attributeQueryBuilderValue !== "") {
                newAttributeQueryBuilderValue = attributeQueryBuilderValue + " " + name + ",0,100,&";
            } else {
                newAttributeQueryBuilderValue = name + ",0,100,&";
            }
        }

        setQueryBuilderValue(newAttributeQueryBuilderValue);
        setUpdate(true);
        makeDirty();
    }

    function getQueryBuilderEntries() {
        let entries = attributeQueryBuilderValue.split(" ");
        return (
            <Grid container>
                {entries.map((item: string, index: number) => {
                    let subItems = item.split(",");
                    if (subItems.length !== 4) return "";
                    let isNumeric = !isAlphaNumeric(attributeValues, subItems[0]);
                    return (
                        <Grid key={index} container direction="row" className={classes.attributeRow}>
                            <Grid item xs={5}>
                                <CustomSelect
                                    value={
                                        isMultiple && isLimboState(selectedLayers, "attribute_query_builder")
                                            ? "Multi"
                                            : subItems[0]
                                    }
                                    className={common.customSelect}
                                >
                                    <MenuItem value="Multi" style={{ display: "none" }}>
                                        Multi
                                    </MenuItem>
                                    <MenuItem
                                        onClick={(evt: any) => {
                                            removeAttribute(index);
                                        }}
                                    >
                                        <HighlightOff style={{ marginRight: 10 }} />
                                        Remove
                                    </MenuItem>
                                    <Divider />
                                    <MenuItem value="all" style={{ display: "none" }} disabled>
                                        <em></em>
                                    </MenuItem>
                                    {attributes.map((attribute: any) => {
                                        return (
                                            <MenuItem
                                                key={"attr-" + attribute.index + "-" + attribute.name}
                                                value={attribute.name}
                                                onClick={(evt: any) => {
                                                    setAttribute(attribute.index, subItems[0]);
                                                }}
                                            >
                                                {attribute.label}
                                            </MenuItem>
                                        );
                                    })}
                                </CustomSelect>
                            </Grid>
                            <Grid item xs={2} style={{ display: isNumeric ? "block" : "none" }}>
                                <DashInput
                                    name={layer.ID + "-minAttribute-" + index}
                                    className={classes.numberInput}
                                    value={parseInt(subItems[1])}
                                    style={{ width: 32, height: 24, left: 9, top: 0 }}
                                    inputProps={{
                                        min: 0,
                                        style: { textAlign: "right" },
                                    }}
                                    onChange={(evt: any) => setAttributeMin(index, evt.target.value)}
                                    isLimbo={isMultiple && isLimboState(selectedLayers, "attribute_query_builder")}
                                />
                            </Grid>
                            <Grid item xs={3} style={{ display: isNumeric ? "block" : "none" }}>
                                <DashInput
                                    name={layer.ID + "-maxAttribute-" + index}
                                    className={classes.numberInput}
                                    value={parseInt(subItems[2])}
                                    style={{ width: 32, height: 24, left: 20, top: 0 }}
                                    inputProps={{
                                        min: parseInt(subItems[1]),
                                        style: { textAlign: "right" },
                                    }}
                                    onChange={(evt: any) => setAttributeMax(index, evt.target.value)}
                                    isLimbo={isMultiple && isLimboState(selectedLayers, "attribute_query_builder")}
                                />
                            </Grid>
                            <Grid item xs={5} style={{ display: isNumeric ? "none" : "block" }}>
                                <CustomSelect
                                    value={subItems[2]}
                                    className={common.customSelect}
                                    inputProps={{ style: { textAlign: "right" } }}
                                >
                                    {getValues(subItems[0]).map((value: string, index1: number) =>
                                        value !== "VoxelfarmAttribute" &&
                                        value.toLowerCase() !== "campaign" &&
                                        value.toLowerCase() !== "projectcode" ? (
                                            <MenuItem
                                                key={"attr-" + value + "-" + index1}
                                                value={value}
                                                onClick={(evt: any) => {
                                                    setAttributeString(index, value);
                                                }}
                                            >
                                                {value}
                                            </MenuItem>
                                        ) : (
                                            ""
                                        )
                                    )}
                                </CustomSelect>
                            </Grid>
                            <Grid item xs={2}>
                                <Button
                                    title={getOperatorLabel(subItems[3])}
                                    className={classes.booleanPill}
                                    onClick={(evt: any) => {
                                        toggleAttributeOperator(index);
                                    }}
                                >
                                    {getOperatorLabel(subItems[3])}
                                </Button>
                            </Grid>
                        </Grid>
                    );
                })}
                <Grid container direction="row" className={classes.attributeRow}>
                    <Grid item xs={5}>
                        <CustomSelect
                            inputProps={{ style: { width: "100%" } }}
                            value={
                                isMultiple && isLimboState(selectedLayers, "attribute_query_builder") ? "Multi" : "None"
                            }
                            className={common.customSelect}
                            style={{ color: "silver", fontStyle: "italic" }}
                        >
                            <MenuItem value="None">None</MenuItem>
                            <MenuItem value="Multi" style={{ display: "none" }}>
                                Multi
                            </MenuItem>
                            <Divider />
                            <MenuItem value="all" style={{ display: "none" }} disabled>
                                <em></em>
                            </MenuItem>
                            {attributes.map((attribute: any, index: number) => {
                                if (
                                    attribute.name !== "voxelfarm_attribute" &&
                                    attribute.name.toLowerCase() !== "campaign" &&
                                    attribute.name.toLowerCase() !== "projectcode"
                                ) {
                                    return (
                                        <MenuItem
                                            key={"attribute-" + index}
                                            value={attribute.name}
                                            onClick={() => {
                                                addAttributeFilterRow(attribute.name);
                                            }}
                                        >
                                            {attribute.label}
                                        </MenuItem>
                                    );
                                } else {
                                    return "";
                                }
                            })}
                        </CustomSelect>
                        <FormHelperText>Select Attribute</FormHelperText>
                    </Grid>
                </Grid>
            </Grid>
        );
    }

    function showAll() {
        setShowMore(false);
    }

    function showLess() {
        setShowMore(true);
    }

    function setFilterValue(value: string) {
        var textArea = document.querySelector("textarea");
        if (textArea) {
            textArea.value = textArea.value + value + " ";
            textArea.focus();
        }
    }

    function applySettings() {
        setNeedsApply(false);
        const isBlockModel = requiresServerUpdate(attributeQueryEntity, currentProject);
        //But need to ensure it is dirty
        if (attributeQueryMode === 1) {
            updateAttributeQueryBuilder();
        } else if (attributeQueryMode === 2) {
            updateAttributeQueryString();
        }
        if (isBlockModel){
            handleLayerEvent("refreshView", null, layer);
        }
    }

    const handleTabChange = (e: any, newValue: number) => {
        if (newValue === 1) {
            setUseQueryBuilder();
        } else {
            setUseQueryString();
        }
    };

    return (
        <Section name="attributeFilter" label="Attribute Filter">
            <div className={classes.content} style={{ marginLeft: -10 }}>
                <Tabs
                    value={attributeQueryMode}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className={common.tabs}
                >
                    <Tab value={1} label="Query Builder" className={common.tab} />
                    <Tab value={2} label="Query Text" className={common.tab} />
                </Tabs>
                <div
                    id="queryBuilderContainer"
                    className={classes.subSectionContainer}
                    style={attributeQueryMode === 1 ? { display: "block", marginTop: 15 } : { display: "none" }}
                >
                    <Typography className={classes.subSectionLabel}>Add Attributes</Typography>
                    {getQueryBuilderEntries()}
                </div>
                <div
                    className={classes.subSectionContainer}
                    style={attributeQueryMode === 2 ? { display: "block", marginTop: 15 } : { display: "none" }}
                >
                    <Typography className={classes.subSectionLabel}>Write Query</Typography>
                    <textarea
                        defaultValue={attributeQueryString}
                        style={{
                            resize: "none",
                            width: "100%",
                            backgroundColor: alpha(theme.palette.background.default, 0.65),
                            color : 'silver',
                            border: "none",
                            padding: 7,
                        }}
                        rows={5}
                        onChange={(evt: any) => {
                            setQueryString(evt.target.value);
                        }}
                    ></textarea>
                    <Typography className={classes.hintLabel}>Available Attributes and operations</Typography>
                    <div>
                        {attributes.map((item: any, index: number) => {
                            if (showMore) {
                                if (index + 1 <= maxAttributesShown) {
                                    return (
                                        <Button
                                            key={"button-" + item.name}
                                            title={item.name}
                                            onClick={() => setFilterValue(item.name)}
                                            style={{
                                                margin: 2,
                                                overflow: "visible",
                                                borderRadius: 16,
                                                fontSize: 12,
                                                minWidth: 40,
                                                height: 20,
                                                textTransform: "none",
                                                backgroundColor: "rgba(0,39,118,0.08)",
                                            }}
                                        >
                                            {item.label}
                                        </Button>
                                    );
                                }
                            } else {
                                return (
                                    <Button
                                        key={"button-" + item.name}
                                        title={item.name}
                                        onClick={() => setFilterValue(item.name)}
                                        style={{
                                            margin: 2,
                                            overflow: "visible",
                                            borderRadius: 16,
                                            fontSize: 12,
                                            minWidth: 40,
                                            height: 20,
                                            textTransform: "none",
                                            backgroundColor: "rgba(0,39,118,0.08)",
                                        }}
                                    >
                                        {item.name}
                                    </Button>
                                );
                            }
                            return "";
                        })}
                        {showMore ? (
                            <div className={classes.showMore} onClick={showAll}>
                                Show More
                            </div>
                        ) : getValues(layer.ID).length > maxAttributesShown ? (
                            <div className={classes.showMore} onClick={showLess}>
                                Show Less
                            </div>
                        ) : (
                            ""
                        )}
                    </div>
                </div>
                {needsApply ? (
                    <div className={classes.bottomButtonContainer}>
                        <Button
                            onClick={applySettings}
                            title="This panel contains changes that were not applied to the viewer. Click this button to update the view."
                        >
                            Apply
                        </Button>
                    </div>
                ) : (
                    ""
                )}
            </div>
        </Section>
    );
}
